/**
 * compare.ts — the version checker's PURE core: manifest parsing + the
 * drift-comparison helpers. Vite-free on purpose (the baked-manifest import
 * lives in manifest.ts) so the node-level test suite can import this module
 * directly (test/version.test.ts).
 */

export interface VersionManifest {
  schema: number;
  version: string;
  builtAt: string;
  frontend: { sourceMd5: string | null; distMd5: string | null };
  images: { api: string; jupyter: string };
}

export interface LiveServiceInfo {
  image: string;
  digest: string;
  source: 'registry' | 'local';
  workingDir?: string;
  configFile?: string;
  project?: string;
}

export interface LiveVersionInfo {
  api: LiveServiceInfo | null;
  jupyter: LiveServiceInfo | null;
  canSelfUpdate: boolean;
}

/** Parse + validate a manifest document (null when malformed/incomplete). */
export function parseManifest(text: string): VersionManifest | null {
  try {
    const j = JSON.parse(text) as Partial<VersionManifest>;
    if (
      typeof j.version !== 'string' ||
      !j.frontend ||
      typeof j.frontend.sourceMd5 !== 'string' ||
      !j.images ||
      typeof j.images.api !== 'string' ||
      typeof j.images.jupyter !== 'string'
    ) {
      return null;
    }
    return {
      schema: typeof j.schema === 'number' ? j.schema : 1,
      version: j.version,
      builtAt: typeof j.builtAt === 'string' ? j.builtAt : '',
      frontend: {
        sourceMd5: j.frontend.sourceMd5,
        distMd5: typeof j.frontend.distMd5 === 'string' ? j.frontend.distMd5 : null,
      },
      images: j.images,
    };
  } catch {
    return null;
  }
}

/** A short display form of a digest/md5 ("sha256:0f607a93…" → "0f607a93"). */
export function shortHash(value: string | null | undefined, n = 8): string {
  if (!value) return '—';
  const h = value.startsWith('sha256:') ? value.slice(7) : value;
  return h.length <= n ? h : `${h.slice(0, n)}…`;
}

/** True when both digests are known and differ — the "outdated" signal.
 *  Unknown (empty/missing) values never flag (a dev build without pushed
 *  images must not nag). */
export function digestsDiffer(
  released: string | null | undefined,
  live: string | null | undefined,
): boolean {
  if (!released || !live) return false;
  return released !== live;
}

/** The stack-drift signal the checker derives: the SERVED release digests vs
 *  the LIVE backend ones. Registry-pulled stacks only — a locally-built
 *  stack has no registry digest to compare, so the answer is "unknown"
 *  (null). A missing live jupyter container is not drift on its own. */
export function stackDrift(
  served: VersionManifest | null,
  live: LiveVersionInfo | null,
): boolean | null {
  if (!served || !live?.api || live.api.source !== 'registry') return null;
  if (!served.images.api && !served.images.jupyter) return null;
  return (
    digestsDiffer(served.images.api, live.api.digest) ||
    (live.jupyter ? digestsDiffer(served.images.jupyter, live.jupyter.digest) : false)
  );
}

/** The frontend-drift signal: the BAKED sourceMd5 (what this tab runs) vs
 *  the SERVED one (what the deploy runs). null = unknown (no manifest on
 *  one side — an older deploy or a broken bake). */
export function frontendDrift(
  baked: VersionManifest | null,
  served: VersionManifest | null,
): boolean | null {
  if (!baked || !served) return null;
  return served.frontend.sourceMd5 !== baked.frontend.sourceMd5;
}
